import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import noop from 'lodash/noop';
import { useEffect, useMemo, useRef } from 'react';
import { v4 } from 'uuid';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import { Stack, useToast } from '@eluve/components';
import {
  amendmentFragment,
  appointmentDoctorInteractionFragment,
  appointmentPatientFragment,
  baseAppointmentInfoFragment,
  useAmendments,
  useAppointmentPatient,
  useIsAppointmentReadonly,
  useIsUserAppointmentOwner,
  useSummary,
  useSummaryContentRef,
} from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { useTranslation } from '@eluve/i18n';
import { useAnalytics } from '@eluve/posthog-react';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  DynamicArtifactRenderer,
  DynamicArtifactSaveContextProps,
  FeatureFlaggedComponent,
  SummaryFeedbackModal,
  SummarySectionMarkdown,
} from '@eluve/smart-blocks';

import { AppointmentBillingCodes } from './AppointmentBillingCodes';
import { FHIRMedicineRecommendationsContainer } from './AppointmentMedicine';

export const signedAppointmentLayoutFragment = graphql(
  `
    fragment SignedAppointmentLayout on Appointments {
      ...baseAppointmentInfo
      ...appointmentPatient
      ...appointmentDoctorInteraction
    }
  `,
  [
    baseAppointmentInfoFragment,
    appointmentPatientFragment,
    appointmentDoctorInteractionFragment,
  ],
);

interface SignedSummaryResultProps {
  appointmentId: string;
  compact?: boolean;
}

const createChartAmendmentMutation = graphql(`
  mutation createChartAmendment($amendmentId: uuid!, $keyPath: String!) {
    insertChartAmendmentsOne(
      object: { amendmentId: $amendmentId, keyPath: $keyPath }
    ) {
      __typename
      id
      amendmentId
      keyPath
    }
  }
`);

export const SignedSummaryResult: React.FC<SignedSummaryResultProps> = ({
  appointmentId,
  compact,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const contentRef = useRef<HTMLDivElement>(null);
  const { setContentRef } = useSummaryContentRef();
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();
  const isReadonly = useIsAppointmentReadonly();
  const isUserApptOwner = useIsUserAppointmentOwner();
  const analytics = useAnalytics('amendments');

  useEffect(() => {
    if (contentRef.current) {
      setContentRef(contentRef.current);
    }

    return () => {
      setContentRef(null);
    };
  }, [contentRef, setContentRef]);

  const [createChartAmendment] = useMutation(createChartAmendmentMutation, {
    onError: () => toast.error(t('appointment.signedSummary.amendmentFailed')),
    optimisticResponse: (data) => ({
      insertChartAmendmentsOne: {
        __typename: 'ChartAmendments' as const,
        id: v4(),
        amendmentId: data.amendmentId,
        keyPath: data.keyPath,
      },
    }),
    update: (_, { data }) => {
      if (!data?.insertChartAmendmentsOne) {
        return null;
      }
      cacheUtils.updateFragment(
        {
          fragment: amendmentFragment,
          key: { id: data?.insertChartAmendmentsOne?.amendmentId },
        },
        (existing) => {
          if (!existing || !data?.insertChartAmendmentsOne) {
            return null;
          }
          return produce(existing, (draft) => {
            draft.chart_amendments.push({
              __typename: 'ChartAmendments' as const,
              id: data.insertChartAmendmentsOne!.id,
              value: null,
              keyPath: data.insertChartAmendmentsOne!.keyPath,
              amendmentId: data.insertChartAmendmentsOne!.amendmentId,
              tenantId,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            });
          });
        },
      );
    },
  });

  const appointment = useCompleteFragment({
    fragment: signedAppointmentLayoutFragment,
    key: {
      id: appointmentId,
    },
  });

  const patient = useAppointmentPatient();

  const interaction = appointment.doctor_interaction;

  const { summary, llmOutputId, isSummaryAvailable } = useSummary();

  const { amendments: draftAmendments } = useAmendments(appointmentId, {
    draftOnly: true,
  });
  const amendmentId = draftAmendments.length
    ? draftAmendments[0]!.id
    : undefined;

  const insertChartAmendment:
    | DynamicArtifactSaveContextProps['insertChartAmendment']
    | undefined = useMemo(() => {
    if (!amendmentId) {
      return undefined;
    }
    return async (keyPath: string) => {
      await createChartAmendment({
        variables: {
          amendmentId,
          keyPath,
        },
        onCompleted: () => {
          analytics.capture('user_inserted_amendment', {
            chartSectionAmending: keyPath,
            amendmentId,
            appointmentId,
            tenantId,
            userId,
          });
        },
      });
    };
  }, [createChartAmendment, amendmentId]);

  return (
    <Stack className="p-4" justify="between" align="stretch" gap={2}>
      <Stack
        className="mb-10 md:gap-6"
        ref={contentRef}
        fullWidth={false}
        gap={4}
        align="stretch"
      >
        {isSummaryAvailable && (
          <div data-accessibility-label="note-summary">
            <DynamicArtifactRenderer
              blocks={summary!.blocks}
              isReadonly={true}
              compact={compact}
              insertChartAmendment={insertChartAmendment}
              email={
                patient?.email || patient?.external_patients_info?.[0]?.email
              }
              canViewAndEditAmendments={isUserApptOwner}
            />
          </div>
        )}

        {interaction?.additionalNotes && (
          <SummarySectionMarkdown
            key="AdditionalNotes"
            // TODO(jesse)[ELU-949]: This should be presentational only and doesn't need to reuse
            // the same component. The fact that we're passing in noop is a red flag
            summaryKey={`AdditionalNotes` as any}
            sectionTitle={t('appointment.signedSummary.additionalNotes')}
            content={interaction.additionalNotes}
            disabled={true}
            handleContentChange={noop}
            flat
            compact={compact}
          />
        )}

        <Stack gap={2} direction="horizontal">
          <SummaryFeedbackModal
            appointmentId={appointmentId}
            llmOutputId={llmOutputId}
          />
        </Stack>

        <FeatureFlaggedComponent flag="BILLING_CODES">
          <AppointmentBillingCodes
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </FeatureFlaggedComponent>

        <FeatureFlaggedComponent flag="MEDICATION">
          <FHIRMedicineRecommendationsContainer
            isReadonly={isReadonly}
            appointmentId={appointmentId}
            tenantId={tenantId}
          />
        </FeatureFlaggedComponent>
      </Stack>
    </Stack>
  );
};
